import { useState, useCallback, useEffect } from 'react';
import axiosInstance from '../axiosInstance';

const CMS_SUBSCRIBERS = '/cms/subscribers';

export function useSubscribersList(params = {}) {
  const [data, setData] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.get(CMS_SUBSCRIBERS, {
        params: {
          limit: params.limit ?? 100,
          page: params.page ?? 1,
          search: params.search,
          active: params.active,
        },
      });
      setData(res?.data?.subscribers ?? []);
      setPagination(res?.pagination ?? null);
      return res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load subscribers';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [params.limit, params.page, params.search, params.active]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return { data, pagination, loading, error, refetch: fetchList };
}

export function useSubscriberById(id) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchOne = useCallback(async () => {
    if (!id) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.get(`${CMS_SUBSCRIBERS}/${id}`);
      setData(res?.data?.subscriber ?? null);
      return res?.data?.subscriber;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load subscriber';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOne();
  }, [fetchOne]);

  return { data, loading, error, refetch: fetchOne };
}

export function useCreateSubscriber() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.post(CMS_SUBSCRIBERS, payload);
      return res?.data?.subscriber ?? res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to create subscriber';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

export function useUpdateSubscriber() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id, payload) => {
    if (!id) throw new Error('Subscriber id is required');
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.put(`${CMS_SUBSCRIBERS}/${id}`, payload);
      return res?.data?.subscriber ?? res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update subscriber';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

export function useUnsubscribeSubscriber() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id, body = {}) => {
    if (!id) throw new Error('Subscriber id is required');
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.post(`${CMS_SUBSCRIBERS}/${id}/unsubscribe`, body);
      return res?.data?.subscriber ?? res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to unsubscribe';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

export function useDeleteSubscriber() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id) => {
    if (!id) throw new Error('Subscriber id is required');
    setLoading(true);
    setError(null);
    try {
      await axiosInstance.delete(`${CMS_SUBSCRIBERS}/${id}`);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to delete subscriber';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}
